import { useEffect, useRef, useState } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';

import { UseVoiceRecorderConfig } from './useVoiceRecorder.types';

export const useVoiceRecorder = ({ onAudioRecorded, onStatusChange }: UseVoiceRecorderConfig = {}) => {
  const rtcRecorderRef = useRef<RecordRTC | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('idle');

  const setAndNotifyStatus = (nextStatus: string) => {
    setStatus(nextStatus);
    onStatusChange?.(nextStatus);
  };

  const clearStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const getMediaStream = (): Promise<MediaStream> => {
    if (navigator.mediaDevices?.getUserMedia) {
      return navigator.mediaDevices.getUserMedia({ audio: true });
    }
    const legacyGetUserMedia =
      (navigator as any).getUserMedia || (navigator as any).webkitGetUserMedia || (navigator as any).mozGetUserMedia;

    if (!legacyGetUserMedia) {
      return Promise.reject(new Error('getUserMedia is not supported in this browser'));
    }

    return new Promise((resolve, reject) => legacyGetUserMedia.call(navigator, { audio: true }, resolve, reject));
  };

  const startRecording = async () => {
    let stream: MediaStream;

    try {
      stream = await getMediaStream();
    } catch (err) {
      setAndNotifyStatus('no_media');

      return;
    }

    mediaStreamRef.current = stream;

    const recorder = new RecordRTC(stream, {
      type: 'audio',
      recorderType: StereoAudioRecorder,
      mimeType: 'audio/wav',
      numberOfAudioChannels: 1,
    });

    rtcRecorderRef.current = recorder;
    recorder.startRecording();
    setAndNotifyStatus('recording');
  };

  const stopRecording = () => {
    if (!rtcRecorderRef.current) {
      return;
    }

    rtcRecorderRef.current.stopRecording(() => {
      const blob = rtcRecorderRef.current?.getBlob();

      if (!blob) {
        setAndNotifyStatus('idle');
        clearStream();

        return;
      }

      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }

      const nextUrl = URL.createObjectURL(blob);
      setMediaBlobUrl(nextUrl);
      onAudioRecorded?.(blob);
      setAndNotifyStatus('stopped');
      clearStream();
      rtcRecorderRef.current = null;
    });
  };

  const clearBlobUrl = () => {
    if (rtcRecorderRef.current) {
      rtcRecorderRef.current.destroy();
      rtcRecorderRef.current = null;
    }

    clearStream();

    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
    }

    setMediaBlobUrl(null);
    setAndNotifyStatus('idle');
  };

  useEffect(
    () => () => {
      if (rtcRecorderRef.current) {
        rtcRecorderRef.current.destroy();
      }
      clearStream();

      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
    },
    [mediaBlobUrl],
  );

  return {
    mediaBlobUrl,
    status,
    startRecording,
    stopRecording,
    clearBlobUrl,
  };
};
